import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check, ShieldCheck, Sparkles, XCircle, Download, ScanLine, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CertificatVerify() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(!!contractId);
  const [manualCode, setManualCode] = React.useState("");

  React.useEffect(() => {
    if (!contractId) return;
    fetch(`${API}/api/certificat/${contractId}/info`)
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data.verified) setInfo(data);
        else setError(data.detail || "Certificat introuvable");
      })
      .catch(() => setError("Erreur réseau"))
      .finally(() => setLoading(false));
  }, [contractId]);

  const submitCode = (e) => {
    e.preventDefault();
    const code = manualCode.trim();
    if (!code) return;
    navigate(`/certificat/verify/${encodeURIComponent(code)}`);
  };

  // Landing UI when no certificate id in URL
  if (!contractId) {
    return (
      <div className="section" data-testid="certificat-landing">
        <div className="container-narrow max-w-xl">
          <div className="rounded-2xl bg-gradient-to-br from-[#5BA4D4] to-[#3d7fb0] text-white p-8 text-center shadow-xl">
            <div className="mx-auto h-20 w-20 rounded-full bg-white/20 flex items-center justify-center">
              <ScanLine className="h-12 w-12" />
            </div>
            <h1 className="mt-5 text-3xl font-bold">Vérification de certificat</h1>
            <p className="mt-2 text-white/90 text-sm">
              Scannez le QR code présent sur votre certificat Pro-pre, ou saisissez la référence ci-dessous.
            </p>
          </div>

          <form onSubmit={submitCode} className="mt-6 rounded-2xl bg-white border border-slate-200 p-6">
            <label htmlFor="cert-code" className="text-sm font-medium text-navy">
              Référence du certificat
            </label>
            <input
              id="cert-code"
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="ex: b1b72d7b-fc83-44c3-91e5-26294b41fcd1"
              className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5BA4D4]"
              data-testid="certificat-code-input"
              autoFocus
            />
            <button
              type="submit"
              className="btn-primary mt-4 w-full justify-center"
              data-testid="certificat-code-submit"
              disabled={!manualCode.trim()}
            >
              Vérifier <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-4 text-xs text-slate-500 text-center">
              La référence figure sous le QR code sur le certificat papier ou PDF.
            </p>
          </form>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-[#5BA4D4] hover:underline" data-testid="certificat-back-home">
              ← Retour à l&apos;accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section" data-testid="certificat-verify-page">
      <div className="container-narrow max-w-xl">
        {loading ? (
          <div className="text-center py-16 text-slate-500">Vérification en cours…</div>
        ) : error ? (
          <div className="rounded-2xl bg-red-50 border-2 border-red-200 p-8 text-center" data-testid="certificat-error">
            <XCircle className="h-12 w-12 text-red-500 mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-navy">Certificat introuvable</h1>
            <p className="mt-3 text-sm text-slate-600">{error}</p>
            <Link to="/" className="btn-outline mt-6 inline-flex">Retour à l&apos;accueil</Link>
          </div>
        ) : (
          <div>
            {/* Verified badge */}
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-8 text-center shadow-xl" data-testid="certificat-verified">
              <div className="mx-auto h-20 w-20 rounded-full bg-white/20 flex items-center justify-center">
                <ShieldCheck className="h-12 w-12" />
              </div>
              <h1 className="mt-5 text-3xl font-bold">Certificat vérifié</h1>
              <p className="mt-2 text-emerald-50 text-sm">
                Ce lieu a bien été nettoyé par <b>Pro-pre Nettoyage</b>
              </p>
            </div>

            {/* Details */}
            <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5 text-[#5BA4D4]" />
                <h2 className="font-semibold text-navy">Détails du certificat</h2>
              </div>
              <dl className="space-y-2 text-sm">
                <Row label="Référence" value={info.contract_id} testid="cert-ref" />
                <Row label="Émis le" value={(info.issued_at || "").slice(0, 10)} testid="cert-issued" />
                <Row label="Date du service" value={info.service_date || "—"} testid="cert-service-date" />
                <Row label="Ville" value={info.city || "—"} testid="cert-city" />
                <Row label="Technicien" value={info.technician} testid="cert-tech" />
                <Row label="Protocole" value={info.protocol} testid="cert-protocol" />
              </dl>
              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-2 text-emerald-700 text-xs">
                <Check className="h-4 w-4" />
                Authenticité vérifiée sur www.pro-pre.com
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <a
                href={`${API}/api/certificat/${contractId}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline"
                data-testid="cert-download-pdf"
              >
                <Download className="h-4 w-4" /> Télécharger PDF
              </a>
              <Link to="/" className="btn-primary" data-testid="cert-back-home">
                Voir Pro-pre
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-2 border-b border-slate-100 py-2" data-testid={testid}>
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-navy text-right">{value}</dd>
    </div>
  );
}
